/**
 * Error Event Component
 *
 * Handles ahoy_errored events (e.g., formula calculation failures).
 * Display pattern: Header with expandable sub-items showing error details.
 */

import { AlertTriangle, FileCode02 } from '@untitledui/icons';
import type { ReactNode } from 'react';
import { useCallback, useMemo } from 'react';

import { EntityBadge } from '@/lib/crm/components/badges/EntityBadge';
import { useOpenSlideoutOptional } from '@/lib/crm/hooks/useOpenSlideout';

import {
  ActorName,
  bold,
  InlineText,
  TimelineEvent,
} from '../components/shared';
import { useViewStateLookups } from '../context/TimelineLookupsContext';
import type { ErrorPayload } from '../types';
import type { SubItemContent } from '../types/renderer';
import { useEventFormatters } from '../useEventFormatters';
import type { EventComponentProps } from './types';

/**
 * Parse error message to extract formula if present.
 * Looks for "Formula: <formula>" at the end of the message.
 */
function parseErrorMessage(message: string): {
  message: string;
  formula?: string;
} {
  const formulaMatch = message.match(/\. Formula: (.+)$/);
  if (formulaMatch) {
    return {
      message: message.replace(/\. Formula: .+$/, ''),
      formula: formulaMatch[1],
    };
  }
  return { message };
}

export function ErrorEvent({
  event,
  entityType: _entityType,
}: EventComponentProps) {
  const { getActorInfo, formatPropertyName, getObjectLabel } =
    useEventFormatters();
  const { lookups } = useViewStateLookups();
  const actor = getActorInfo(event);
  const openSlideout = useOpenSlideoutOptional();

  const payload = event.payload as ErrorPayload | undefined;
  const rawErrorMessage = payload?.error_message || 'An error occurred';
  const propertyName = payload?.property_type_name;

  // Check if this is an associated event (from a different entity)
  const isAssociatedEvent = 'association_type_name' in event;

  // For associated events, get the full object from lookups for badge display
  const associatedObject = isAssociatedEvent
    ? lookups.entities[event.entity_id]
    : undefined;

  const handleAssociatedClick = useCallback(() => {
    if (associatedObject) {
      openSlideout?.(associatedObject.entity_id);
    }
  }, [openSlideout, associatedObject]);

  const associatedBadgeState = useMemo(
    () =>
      associatedObject
        ? {
            kind: 'data' as const,
            data: {
              id: associatedObject.entity_id,
              entityType: associatedObject.entity_type_name,
              displayName: associatedObject.ahoy_display_name ?? 'Unknown',
            },
          }
        : null,
    [associatedObject]
  );

  // For associated events, get the object label from lookups (for aria-label and fallback)
  const objectDisplayText = isAssociatedEvent
    ? getObjectLabel(event.entity_id)
    : undefined;

  // Look up property display name from schema
  const getPropertyDisplayName = (propName: string): string => {
    const key = `${event.entity_type_name}.${propName}`;
    const schema = lookups.property_types[key];
    return (
      schema?.label || formatPropertyName(propName, event.entity_type_name)
    );
  };

  const propertyDisplayName = propertyName
    ? getPropertyDisplayName(propertyName)
    : undefined;

  // Parse error message to extract formula
  const { message: errorMessage, formula } = parseErrorMessage(rawErrorMessage);

  // Build sub-items for error details
  const subItems: SubItemContent[] = [];

  // Add formula if extracted from error message
  if (formula) {
    subItems.push({
      icon: <FileCode02 className='w-4 h-4 text-tertiary' />,
      text: (
        <span>
          <span className='text-tertiary'>Formula:</span>{' '}
          <code className='font-mono text-xs bg-bg-secondary px-1 py-0.5 rounded'>
            {formula}
          </code>
        </span>
      ),
      ariaLabel: `Formula: ${formula}`,
    });
  }

  // Add the error message itself
  subItems.push({
    icon: <AlertTriangle className='w-4 h-4 text-utility-error-500' />,
    text: <span className='text-utility-error-700'>{errorMessage}</span>,
    ariaLabel: errorMessage,
  });

  // Build aria-label with all context
  const ariaLabelParts = [`${actor.name} errored`];
  if (propertyDisplayName) {
    ariaLabelParts.push(`on ${propertyDisplayName}`);
  }
  if (objectDisplayText) {
    ariaLabelParts.push(`on ${objectDisplayText}`);
  }
  const ariaLabel = ariaLabelParts.join(' ');

  let objectDisplay: ReactNode;
  if (associatedBadgeState) {
    objectDisplay = (
      <EntityBadge
        state={associatedBadgeState}
        className='align-middle'
        onClick={openSlideout ? handleAssociatedClick : undefined}
      />
    );
  } else if (isAssociatedEvent && objectDisplayText) {
    objectDisplay = bold(objectDisplayText);
  } else {
    objectDisplay = null;
  }

  return (
    <TimelineEvent ariaLabel={ariaLabel}>
      <TimelineEvent.Icon event={event} />
      <TimelineEvent.Content
        eventId={event.event_id}
        header={
          <InlineText>
            <ActorName actor={actor} /> errored
            {propertyDisplayName && <> on {bold(propertyDisplayName)}</>}
            {objectDisplay && <> on {objectDisplay}</>}
          </InlineText>
        }
        timestamp={event.occurred_at}
        subItems={subItems}
      />
    </TimelineEvent>
  );
}
